export const BUSINESS_SOCIAL_URL_FIELDS = [
  "facebookUrl",
  "instagramUrl",
  "linkedinUrl",
  "tiktokUrl",
  "youtubeUrl",
  "twitterUrl",
] as const;

export type BusinessSocialUrlField = (typeof BUSINESS_SOCIAL_URL_FIELDS)[number];

export type ClassifiedSocialProfile = {
  fieldKey: BusinessSocialUrlField;
  platform: string;
  url: string;
};

const HOST_RULES: Array<{
  fieldKey: BusinessSocialUrlField;
  platform: string;
  hosts: string[];
}> = [
  { fieldKey: "facebookUrl", platform: "Facebook", hosts: ["facebook.com", "fb.com", "fb.me"] },
  { fieldKey: "instagramUrl", platform: "Instagram", hosts: ["instagram.com"] },
  { fieldKey: "linkedinUrl", platform: "LinkedIn", hosts: ["linkedin.com"] },
  { fieldKey: "tiktokUrl", platform: "TikTok", hosts: ["tiktok.com"] },
  { fieldKey: "youtubeUrl", platform: "YouTube", hosts: ["youtube.com", "youtu.be"] },
  { fieldKey: "twitterUrl", platform: "X", hosts: ["twitter.com", "x.com"] },
];

export function isBusinessSocialUrlField(
  value: string,
): value is BusinessSocialUrlField {
  return (BUSINESS_SOCIAL_URL_FIELDS as readonly string[]).includes(value);
}

export function socialFollowersFieldKey(
  fieldKey: BusinessSocialUrlField,
): string {
  return fieldKey.replace(/Url$/, "Followers");
}

function withProtocol(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (trimmed.includes(".")) return `https://${trimmed.replace(/^\/+/, "")}`;
  return null;
}

export function classifySocialProfileUrl(
  raw: string,
): ClassifiedSocialProfile | null {
  const href = withProtocol(raw);
  if (!href) return null;
  try {
    const parsed = new URL(href);
    const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
    const match = HOST_RULES.find((rule) =>
      rule.hosts.some((allowed) => host === allowed || host.endsWith(`.${allowed}`)),
    );
    if (!match) return null;
    parsed.hash = "";
    parsed.username = "";
    parsed.password = "";
    return {
      fieldKey: match.fieldKey,
      platform: match.platform,
      url: parsed.toString(),
    };
  } catch {
    return null;
  }
}
